import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { parseOutline, parseDateAnchors, mergeOutlineAndDates, formatDateAnchorLabel, jumpToOffset } from '../lib/outline';
import { SHORTCUT } from '../utils/platform';
import ShareBadge from './ShareBadge';
import GuestReminderBand from './GuestReminderBand';
import SyntaxBackdrop from './SyntaxBackdrop';
import { createSerialRunner } from '../lib/serialSave';
import { shouldAdvanceBase } from '../lib/baseAdvance';
import { flagKey, isConflictBandVisible } from '../lib/conflictBand';
import HighlightPopup from './HighlightPopup';
import useLineHighlight from '../hooks/useLineHighlight';
import { setFormatTarget, blurFormatTarget } from '../lib/formatTarget';
import HistoryPanel from './HistoryPanel';
import PortalMenu from './PortalMenu';
import { OutlineBand, SharedFromBand, MutedRemindersBand, countOutsideReminders } from './ListPanel';
import { useStickyEditing } from '../hooks/useStickyEditing';
import { useIsOnline } from '../hooks/useIsOnline';
import { hasConflictMarker } from '../lib/mergeConflict';
import { useReminderCapture } from '../hooks/useReminderCapture';
import { isDesignatedReminderList, isDesignatedLogList, getListEmptyHint } from '../hooks/useUserPrefs';
import { applyListSizeGuard } from '../lib/listLimits';
import { useEditLock } from '../hooks/useEditLock';
import { stripRecurrenceGlyph } from '../lib/formatBellLine';
// Merge-on-receive (Walter 2026-08-07) — mirror of ListPanel's wiring.
import { decideAdoption } from '../lib/mergeOnReceive';
import { getClientId } from '../lib/clientId';
import { logSync, fingerprint } from '../lib/syncLog';
import { recordContent } from '../lib/contentTrail';
import { noteShrink, removedLines, countLines } from '../lib/shrinkLog';
import { archiveDivergedLocal, archivePreMerge } from '../lib/historyConflict';
import { moveLinesUp, moveLinesDown } from '../lib/moveLines';
import { getListColorValue, getListColorTextValue, getListColorTint, getListColorHeaderBg, getListColorBodyBg, LIST_COLORS } from '../lib/listColors';
import MarkdownEditor, { dispatchDocReplace } from './MarkdownEditor';
import { isLivePreviewEditorActive } from '../lib/editorFlag';

/**
 * Full-viewport-height focused/reading view for a single list.
 * Left column: outline of headers (clickable → scrolls right column).
 * Right column: the list's textarea (editable, same save path as compact view).
 */
export default function FocusedPanel({
  list,
  onUpdate,
  onExit,
  onSaveStatus,
  // Walter 2026-05-15: same wiring as ListPanel — registers flushSave
  // with the parent so the SaveIndicator's tap-to-force-sync flushes
  // this panel along with any others.
  registerPendingFlush,
  jumpTarget,
  onJumpHandled,
  // When true, render as an in-flow panel that fills its parent flex
  // container (no backdrop, no fixed positioning, no outline sidebar).
  // Used by the mobile layout where the tile sits next to a nav strip
  // instead of floating over the canvas.
  inline = false,
  // Move-to-board support: the header gets a `⋯` menu whose items are the
  // other boards. Hidden if there's nowhere to move to.
  boards = [],
  currentBoardId,
  onMoveList,
  // Lifecycle actions available from the `⋯` menu when rendered in inline
  // (mobile) mode. On desktop these live on the canvas tile itself, so the
  // menu hides them unless at least one handler is provided.
  onArchive,
  onUnarchive,
  onDelete,
  // Reminder capture wiring (same as ListPanel).
  userPrefs,
  updateUserPrefs,
  createReminder,
  reminders,
  updateReminder,
  deleteReminder,
  // Walter 2026-06-11: organize-reminders flow, mirrored from ListPanel
  // so the FOCUSED view of a reminder list offers the same sort/organise
  // affordance ("sort should be in both regular and focused"). Walter
  // 2026-07-04: organize now applies directly with an undo toast — the
  // old Accept/Undo band + edit lock were removed as needless friction.
  onTriggerOrganize,
  pushToast,
  // Edit-lock plumbing — same shape as ListPanel. See useEditLock for
  // semantics. Lock engages only when sharingAlphaEnabled AND the
  // board has at least one collaborator.
  currentUid,
  currentUserName,
  // Walter 2026-05-28: see ListPanel for full rationale — guest's
  // reminder-list body renders dim as an ambient signal that
  // anonymous reminders aren't actually firing.
  isGuest = false,
  onRequestSignIn,
  boardOwnerUid,
  boardEditors = [],
  boardViewers = [],
  // Open the Share modal for this list. Null when sharing alpha is off.
  onShareList,
  // Peek share state on this list (always wired). Backs the inline
  // ShareBadge in the focused-view header.
  onPeekShare,
  // Walter 2026-05-11: opens the board-picker so the user can place
  // this shared list inside one of their own boards. Mirrors the
  // ListPanel prop; gated on `isSharedWithMe` below.
  onPlaceList,
  // True when the current user is NOT the owner of this list (either
  // it lives on a shared board or it's a placed-shared-list on one
  // of the user's own boards).
  isSharedWithMe = false,
  // Walter 2026-05-05 (slide 1): when this is the user's designated
  // reminder list, the focused-view header shows a clickable count
  // ("N active, M here, K elsewhere") that opens the structured
  // reminder view (agenda panel). Wired from BoardPage.
  onOpenAgenda,
  // Walter 2026-05-11: opens the structured edit popover for the
  // reminder whose 🔁 was clicked. Mirrors the ListPanel prop.
  onOpenReminderEditor,
}) {
  const textareaRef = useRef(null);
  const mdEditorRef = useRef(null);
  // Experimental live-preview (CodeMirror-6) editor, opt-in per device. Read
  // once at render — toggling it in Settings reloads the page (see editorFlag).
  const useLivePreview = isLivePreviewEditorActive();
  const titleInputRef = useRef(null);
  const saveTimer = useRef(null);
  // Walter 2026-07-04: pulse the "take over" button when the user clicks
  // into a list locked by someone else (mirrors ListPanel).
  const [takeoverFlash, setTakeoverFlash] = useState(false);
  const takeoverFlashTimer = useRef(null);
  useEffect(() => () => { if (takeoverFlashTimer.current) clearTimeout(takeoverFlashTimer.current); }, []);
  const flashTakeover = () => {
    setTakeoverFlash(true);
    if (takeoverFlashTimer.current) clearTimeout(takeoverFlashTimer.current);
    takeoverFlashTimer.current = setTimeout(() => setTakeoverFlash(false), 1400);
  };
  const pendingPatch = useRef(null); // merged patch waiting to be flushed
  const moveMenuRef = useRef(null);
  // Phase 3 step 2: full-screen note mode. Per-session (no localStorage),
  // toggled from a header button. When true, the focused panel covers
  // the entire viewport — even on the desktop variant which is normally
  // a centered overlay sized at min(92vw, 1100px). Mobile inline path
  // also gets bumped to a fixed overlay so app chrome (top header,
  // peek strip) hides behind. Esc exits full-screen first; another
  // Esc closes the panel.
  const [fullScreen, setFullScreen] = useState(false);
  // Reset full-screen whenever the panel is first opened on a new list
  // — Walter wanted opt-in per session, not sticky across notes. The
  // dependency on `list?.id` does the resetting since FocusedPanel
  // re-renders for each list.
  useEffect(() => { setFullScreen(false); }, [list?.id]);
  // Ref to the tile's own header bar. Used by the header-visibility observer
  // below so we can fall back to a floating outline button when the main
  // header scrolls out of view (e.g. iOS keyboard pushing it above the
  // viewport).
  const headerBarRef = useRef(null);

  const [localContent, setLocalContent] = useState(list.content || '');
  // Walter 2026-06-15: 3-way conflict BASE — raw cloud content this buffer
  // descends from. Editor-side (not the timestamp baseline). See ListPanel
  // for the full rationale; mirrored here so the focused editor gets the
  // same silent-overwrite protection.
  const baseContentRef = useRef(list.content || '');
  // Walter 2026-09-08: versions this panel has held, oldest first — see the
  // matching block in ListPanel and isStaleEcho in lib/mergeOnReceive. Every
  // assignment to baseContentRef goes through setBase so the two can't drift.
  const versionHistoryRef = useRef([]);
  // Walter 2026-10-31: texts THIS WINDOW has written. `lastWriteByClientId`
  // identifies the DEVICE, and he runs two windows on one PC - so on its own
  // it would brand the other window's deliberate undo as our own echo and
  // ignore it, trading one kind of disagreement for another. Combined with
  // this set, the echo guard (#250) fires only for text we actually wrote.
  const wroteRef = useRef(new Set());
  const noteWritten = (text) => {
    const s = String(text ?? '');
    wroteRef.current.add(s);
    if (wroteRef.current.size > 8) wroteRef.current.delete(wroteRef.current.values().next().value);
  };

  const setBase = useCallback((text) => {
    const t = String(text ?? '');
    baseContentRef.current = t;
    const h = versionHistoryRef.current;
    if (h.length === 0 || h[h.length - 1].text !== t) {
      h.push({ text: t, at: Date.now() });
      if (h.length > 8) h.shift();
    }
  }, []);
  // Walter 2026-08-07 (merge-on-receive): ref mirror of localContent so the
  // sync effect can read the current buffer without depending on it (which
  // would re-run the effect on every keystroke). See ListPanel for the full
  // rationale; read-only inside the effect.
  const localContentRef = useRef(list.content || '');
  useEffect(() => { localContentRef.current = localContent; }, [localContent]);
  // Which list the sync effect last reconciled. This panel is REUSED for a
  // different list without unmounting (the mobile detail pane swaps `list`
  // while it stays open), and merging list A's buffer into list B would be a
  // spectacular way to lose data — on a switch we always plain-adopt.
  const syncedListIdRef = useRef(list.id);
  // Walter 2026-05-16: history-panel modal state. Mirrored from ListPanel.
  const [showHistory, setShowHistory] = useState(false);
  // Walter 2026-09-14 (fix 2): the conflict flag this panel just resolved.
  // "Mark resolved" clears it in one atomic write, but this panel only hears
  // about that write through its normal snapshot, which can trail by seconds,
  // so the band used to linger and appear to come back. Remembering the exact
  // flag hides it at once, while a genuinely new conflict (a new
  // serverTimestamp) still shows. lib/conflictBand.
  const [dismissedConflictKey, setDismissedConflictKey] = useState(null);
  // Re-runs the snapshot-sync effect after a refused-while-typing snapshot
  // (Walter 2026-07-19 two-PC divergence — see the effect + ListPanel mirror).
  const [syncRetryTick, setSyncRetryTick] = useState(0);

  // Edit-lock — engage whenever the board is shared. NOT gated behind
  // the local user's alpha flag; an invitee who hasn't opted in must
  // still respect the lock or two people could edit simultaneously.
  // See ListPanel for the longer rationale.
  // Walter 2026-05-12: require an OTHER user (not just currentUid) —
  // see ListPanel for the full rationale.
  const hasOtherCollaborator = (uids) =>
    Array.isArray(uids) && uids.some(u => u && u !== currentUid);
  // Walter 2026-05-16: lock always-on (was gated to shared). See
  // ListPanel for the rationale — per-device clientId makes the lock
  // prevent multi-device conflicts on solo lists too.
  const hasSharedCollaborator = !!(
    hasOtherCollaborator(boardEditors) ||
    hasOtherCollaborator(boardViewers) ||
    hasOtherCollaborator(list?.editors) ||
    hasOtherCollaborator(list?.viewers)
  );
  const lockEnabled = true;
  // Walter 2026-05-11: see ListPanel for the long rationale. Placed
  // shared lists carry _sharedFromOwnerUid / _sharedFromBoardId so
  // the lock doc lives at the owner's path (where the list actually
  // is), not the invitee's empty subtree path.
  const lockOwnerUid = list?._sharedFromOwnerUid || boardOwnerUid || currentUid;
  const lockBoardId = list?._sharedFromBoardId || currentBoardId;
  const lock = useEditLock({
    ownerUid: lockOwnerUid,
    boardId: lockBoardId,
    list,
    currentUid,
    currentUserName,
    enabled: lockEnabled,
    onKicked: (byName) => {
      pushToast?.(`${byName} took over editing "${list?.title?.trim() || 'this list'}"`, null, { ttlMs: 5000 });
    },
  });

  // Role-based write gate. Mirrors the ListPanel logic — see comment
  // there. Three ways to qualify as an editor: owner, board-level
  // editor, or per-list editor. Anyone else (incl. viewers) is
  // read-only. effectiveReadOnly combines role and soft-lock.
  const roleCanEdit = useMemo(() => {
    if (!currentUid) return false;
    if (boardOwnerUid && boardOwnerUid === currentUid) return true;
    if (Array.isArray(boardEditors) && boardEditors.includes(currentUid)) return true;
    if (Array.isArray(list?.editors) && list.editors.includes(currentUid)) return true;
    return false;
  }, [currentUid, boardOwnerUid, boardEditors, list?.editors]);
  // Connectivity gate (see ListPanel for full rationale): shared lists
  // become read-only when offline. Own lists stay editable.
  const isOnline = useIsOnline();
  // Walter 2026-05-16: only block writes when offline AND it's a
  // shared list. Solo lists stay editable offline (Firestore queues).
  const offlineLockedShared = hasSharedCollaborator && !isOnline;
  const effectiveReadOnly = !roleCanEdit
    || (lockEnabled && !lock.canEdit)
    || offlineLockedShared;

  // Sticky "actively editing" — see ListPanel for the same pattern
  // and full rationale (Walter's s2b round 6: lock.heldByMe flickers
  // on mobile during snapshot lapses; this layer keeps the band
  // visible across them as long as the user typed in the last 10s).
  const [isStickyEditing, markEditing] = useStickyEditing(10000);

  // Reminder capture (same flow as ListPanel — see useReminderCapture).
  const reminderCapture = useReminderCapture({
    list,
    boardId: currentBoardId,
    // Bug #12 — see ListPanel for full rationale; lets the hook
    // populate aclUids on each createReminder.
    currentUid,
    userPrefs,
    updateUserPrefs,
    createReminder,
    updateReminder,
    reminders,
    deleteReminder,
    pushToast,
    onContentMutate: ({ from, to, insert }) => {
      // CM6 (live-preview) owns the document — apply the edit to the view so the
      // caret maps correctly and we don't fight the localContent mirror (which
      // drifts a tick behind the view). Walter 2026-07-13: fixes the missing 🔔
      // / deleted line when a reminder is captured on Enter. onChange then syncs
      // localContent + schedules the save.
      const cmView = mdEditorRef.current?.view;
      if (useLivePreview && cmView) {
        const docLen = cmView.state.doc.length;
        const safeTo = Math.max(0, Math.min(to, docLen));
        const safeFrom = Math.max(0, Math.min(from, safeTo));
        cmView.dispatch({ changes: { from: safeFrom, to: safeTo, insert } });
        return;
      }
      setLocalContent(prev => {
        const next = prev.slice(0, from) + insert + prev.slice(to);
        scheduleSave({ content: next });
        const delta = insert.length - (to - from);
        const ta = textareaRef.current;
        if (ta) {
          const cur = ta.selectionStart || 0;
          let newCur;
          if (cur < from) newCur = cur;
          else if (cur > to) newCur = cur + delta;
          else newCur = from + insert.length;
          requestAnimationFrame(() => {
            try { ta.setSelectionRange(newCur, newCur); } catch {}
          });
        }
        return next;
      });
    },
  });
  const [localTitle, setLocalTitle] = useState(list.title || '');
  const [editingTitle, setEditingTitle] = useState(false);
  // Mobile-only: collapsible outline drawer. On desktop the outline lives in
  // a permanent left column (see below), so this flag is a no-op there.
  // outlineOpen state removed (round 5): the legacy mobile drawer
  // + floating outline FAB are gone. Mobile uses OutlineBand below
  // header now (same component as canvas tiles), desktop has its
  // permanent left-column outline.
  // `⋯` overflow menu for rename / archive / move / delete. The menu has a
  // two-mode flip: 'main' shows the four actions (with "move to →"
  // navigating into the second mode), 'move' shows the list of other boards
  // to pick a destination. Same UX as the desktop tile (ListPanel) — keeps
  // mobile from drowning users in a long inline board list.
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [menuMode, setMenuMode] = useState('main'); // 'main' | 'move' | 'color'
  // Mobile-only: is the tile's own header bar currently in the viewport?
  // Defaults to true since the header renders inside the initial viewport.
  // Flipped to false by an IntersectionObserver when the header scrolls off
  // (for example, because iOS Safari pushed the page up to keep the focused
  // textarea visible above the virtual keyboard). When false, we render a
  // floating outline button so the outline is still one tap away.
  const [headerVisible, setHeaderVisible] = useState(true);
  const otherBoards = useMemo(
    () => (boards || []).filter(b => b.id !== currentBoardId),
    [boards, currentBoardId],
  );
  // Whether the `⋯` overflow menu should render at all. Move-to-board is
  // available on both desktop and mobile. Archive/unarchive/delete only
  // appear on mobile (inline) since the desktop canvas tile has its own
  // controls for those.
  const isArchived = list.state === 'archived';
  const showMoveSection = !!onMoveList && otherBoards.length > 0;
  const showArchiveItem = inline && !isArchived && !!onArchive;
  const showUnarchiveItem = inline && isArchived && !!onUnarchive;
  const showDeleteItem = inline && !!onDelete;
  // The menu now always renders — rename is always an available action
  // so there's always at least one entry. The other sections (move,
  // archive, delete) are conditional but the menu stays visible.
  const showOverflowMenu = true;

  // Walter 2026-05-17: outside-click dismiss is now handled inside
  // <PortalMenu> (pointerdown + touchstart in capture phase). We
  // still need to reset menuMode on close so reopening doesn't land
  // on the move sub-menu.
  useEffect(() => {
    if (!moveMenuOpen) setMenuMode('main');
  }, [moveMenuOpen]);

  // Track whether the tile's own header bar is currently in the viewport.
  // We only need this on mobile (inline) — desktop has no "header scrolls
  // off" scenario since the focused panel is a fixed overlay. `threshold: 0`
  // flips to false as soon as the header is fully above/below the viewport
  // (any pixel still showing keeps the primary button reachable).
  useEffect(() => {
    if (!inline) return;
    const el = headerBarRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => { setHeaderVisible(!!entry && entry.isIntersecting); },
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inline]);

  // ── Sync from props (Firestore snapshot, other tab, etc.) ──
  useEffect(() => {
    // Walter 2026-05-12: strip legacy "🔔↻" glyphs on read — see
    // ListPanel for full rationale.
    // Walter 2026-05-16: bail guard now requires focus AND recent
    // typing — fixes the multi-device take-over case where the user
    // focused the textarea but hadn't typed yet, so a freshly-arrived
    // remote snapshot from the other device was refused entry. See
    // ListPanel for the longer comment.
    const ta = textareaRef.current;
    // Bail while ACTIVELY typing — but RETRY once the typing window closes
    // (Walter 2026-07-19 two-PC divergence: a snapshot refused mid-typing
    // was never retried → stale base → lines dropped on the next save +
    // silent divergence). See ListPanel mirror for the full story; the 11s
    // retry outlives the 10s sticky window, and the 4s save-debounce means
    // local edits are flushed + echoed before the retry adopts.
    const activelyTyping = isStickyEditing
      && ((ta && document.activeElement === ta) || mdEditorRef.current?.view?.hasFocus);
    if (activelyTyping) {
      const t = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
      return () => clearTimeout(t);
    }
    const raw = list.content || '';
    const stripped = stripRecurrenceGlyph(raw, list.id, reminders);
    // ── MERGE ON RECEIVE (Walter 2026-08-07) — mirror of ListPanel's block;
    // see there (and docs/proposals/2026-07-19-sync-hardening-merge-on-
    // receive.md) for the full rationale. Short version: adopt-by-overwrite
    // is only safe when ONE side moved; when the server moved AND our buffer
    // diverged, merge instead of discarding, and archive before dropping.
    const baseStripped = stripRecurrenceGlyph(baseContentRef.current, list.id, reminders);
    const switchedList = syncedListIdRef.current !== list.id;
    syncedListIdRef.current = list.id;
    const decision = (effectiveReadOnly || switchedList)
      ? { action: 'adopt', content: stripped }
      : decideAdoption({
        base: baseStripped,
        local: localContentRef.current,
        server: stripped,
        history: versionHistoryRef.current,
        now: Date.now(),
        // Walter 2026-10-12 (#250): our own write coming back is an echo
        // however long it took. See lib/mergeOnReceive.isStaleEcho.
        fromSelf: list.lastWriteByClientId === getClientId() && wroteRef.current.has(stripped),
      });

    // Walter 2026-08-24: RECORD THE DECISION. The log had saves and
    // snapshots but not the adopt/merge/conflict choice itself, so when his
    // Reminders list banded on a single online device the log couldn't say
    // which branch fired or what the three versions looked like — it took
    // screenshots of the history modal to reconstruct. Sizes + fingerprints
    // only, same as every other line: never content.
    // Two cases are pure noise and stay out: `keep` (the server didn't
    // move) and an `adopt` whose server value we already hold — the echo of
    // our own save, which happens after every write.
    const boring = decision.action === 'keep'
      || (decision.action === 'adopt' && localContentRef.current === stripped);
    if (!boring) {
      logSync('adopt-decision', {
        list: list.id,
        action: decision.action,
        base: `${baseStripped.length}/${fingerprint(baseStripped)}`,
        local: `${localContentRef.current.length}/${fingerprint(localContentRef.current)}`,
        server: `${stripped.length}/${fingerprint(stripped)}`,
        ...(decision.conflicts ? { clashes: decision.conflicts } : {}),
      });
    }

    if (decision.action === 'stale') {
      // Already moved past this version — a snapshot that lost the race with
      // our own write. Change nothing and leave base alone; see the matching
      // branch in ListPanel. Walter 2026-09-08.
      return;
    }

    if (decision.action === 'keep') {
      // Server unchanged — adopting would only discard unsaved local edits
      // (Walter 2026-08-22: a line typed and not yet saved disappeared here).
      // Base still tracks the server's exact string.
      setBase(raw);
      return;
    }

    if (decision.action === 'merge') {
      // Clean 3-way → converge silently, both sides' edits kept. Base
      // advances to the server value we merged against so the write-back
      // doesn't self-conflict; the other device converges via its own
      // merge-on-receive.
      // Walter 2026-09-03 (content-writer audit): a clean merge is applied
      // silently and used to leave NOTHING behind. "Clean" means the algorithm
      // saw no overlapping edits, not that it got it right — and it reads a
      // moved block as delete+insert (#168), so a misjudged merge dropped
      // lines with no band and no snapshot. One small write buys the pre-merge
      // text back. Fire-and-forget: the net must never delay the merge.
      archivePreMerge({
        uid: lockOwnerUid,
        boardId: lockBoardId,
        listId: list.id,
        content: stripped,
        baseContent: baseStripped,
        actorUid: currentUid,
        source: 'merge-on-receive',
      });
      noteShrink({
        listId: list.id,
        reason: 'merge',
        removed: removedLines(localContentRef.current, decision.content),
        beforeLines: countLines(localContentRef.current),
        afterLines: countLines(decision.content),
      });
      recordContent(list.id, decision.content, 'merge');
      setLocalContent(decision.content);
      setBase(raw);
      scheduleSave({ content: decision.content });
      pushToast?.('merged edits from another device', null, { ttlMs: 5000 });
      return;
    }

    if (decision.action === 'conflict') {
      // Same-line clash → archive OUR buffer + raise the band FIRST, adopt
      // only once that landed. Archive failed, or the user resumed typing
      // mid-round-trip? Keep the buffer and re-arm; never trade the user's
      // text for an unconfirmed round-trip.
      let retry = null;
      let cancelled = false;
      archiveDivergedLocal({
        uid: lockOwnerUid,
        boardId: lockBoardId,
        listId: list.id,
        content: decision.divergedLocal,
        baseContent: baseStripped,
        actorUid: currentUid,
      }).then(({ historyId }) => {
        if (cancelled) return;
        const stillSameBuffer = localContentRef.current === decision.divergedLocal;
        if (!historyId || !stillSameBuffer) {
          retry = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
          return;
        }
        setLocalContent(stripped);
        setBase(raw);
      }).catch((e) => {
        console.warn('[merge-on-receive] archiving the diverged buffer failed (keeping it)', e);
        if (cancelled) return;
        retry = setTimeout(() => setSyncRetryTick(x => x + 1), 11000);
      });
      return () => { cancelled = true; if (retry) clearTimeout(retry); };
    }

    // Walter 2026-08-31: the buffer is about to change without him typing —
    // exactly the shape of the loss he reported (lib/contentTrail).
    // An incoming change that REMOVES lines is the event we're hunting: the
    // user didn't delete them, this window just received a version without
    // them. Always on and long-retained (lib/shrinkLog) — the content trail
    // rolls over in hours and his losses are days apart.
    noteShrink({
      listId: list.id,
      reason: 'adopt',
      removed: removedLines(localContentRef.current, decision.content),
      beforeLines: countLines(localContentRef.current),
      afterLines: countLines(decision.content),
    });
    recordContent(list.id, decision.content, 'adopt');
    setLocalContent(decision.content);
    // Adopted the server's content → it's now the 3-way conflict base.
    // (Guarded above: mid-edit we return before here so base stays on the
    // pre-edit value and a concurrent remote write is caught on save.)
    setBase(raw);
    // Walter 2026-05-18: removed the auto-scheduleSave here that wrote
    // the stripped content back. It was tripping false conflicts when
    // the listener delivered stale cached content on reconnect. See
    // ListPanel for the longer rationale. Display strip stays; the
    // cloud-side migration happens organically on real user edits.
    // eslint-disable-next-line
  }, [list.content, list.id, reminders, syncRetryTick]);
  // Walter 2026-05-18: mirror editingTitle into a ref so the
  // localTitle-sync effect below can read it without putting it in the
  // dep array. See ListPanel for the longer rationale (blur-after-
  // typing was erasing the just-typed title because the sync effect
  // ran on every editingTitle transition and re-read the OLD server
  // title before the listener echoed back).
  const editingTitleRef = useRef(editingTitle);
  useEffect(() => { editingTitleRef.current = editingTitle; }, [editingTitle]);
  useEffect(() => {
    if (editingTitleRef.current) return;
    setLocalTitle(list.title || '');
  }, [list.title]);

  // ── Debounced save with explicit flush ──
  // Mirror of ListPanel's implementation — see comments there for the rationale.
  // Walter 2026-09-14 (fix 2): saves run strictly one after another — this is
  // the panel his PHONE uses (MobileBoard renders FocusedPanel), where the
  // 2026-09-13 false conflict happened. See ListPanel's flushSave for the full
  // story; lib/serialSave for the ordering and its tests.
  const saveRunnerRef = useRef(null);
  if (!saveRunnerRef.current) saveRunnerRef.current = createSerialRunner();
  const flushSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    return saveRunnerRef.current(async () => {
      if (!pendingPatch.current) return;
      const patch = pendingPatch.current;
      pendingPatch.current = null;
      if (onSaveStatus) onSaveStatus('syncing');
      // 3-way conflict base on content writes (stripped centrally in
      // saveList). Read at START, so a queued save sees the advanced base.
      const baseAtStart = baseContentRef.current;
      const outgoing = ('content' in patch)
        ? { ...patch, __baseContent: baseAtStart }
        : patch;
      await onUpdate(list.id, outgoing);
      // Walter 2026-09-19 (#238): never move the base BACKWARDS over an
      // adoption that landed while this save was in flight. See lib/baseAdvance.
      if ("content" in patch && shouldAdvanceBase(baseAtStart, baseContentRef.current)) {
        setBase(patch.content ?? "");
        noteWritten(patch.content ?? "");
      }
      if (onSaveStatus) onSaveStatus('saved');
    });
  }, [list.id, onUpdate, onSaveStatus]);

  const scheduleSave = useCallback((patch) => {
    pendingPatch.current = { ...(pendingPatch.current || {}), ...patch };
    if (onSaveStatus) onSaveStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 4000);
  }, [flushSave, onSaveStatus]);


  // Walter 2026-08-24: ADOPT OUR OWN SORT LOCALLY. organize rewrites every
  // line and used to reach this panel the long way round — through Firestore
  // and back — so the buffer saw "every line moved" as a foreign change and
  // had to merge it against anything typed during the round-trip. That can't
  // merge cleanly, and it handed him a conflict band on a single device with
  // both ends online. Applying the result here means there is no divergence
  // to reconcile: the buffer and the base both become the sorted text, and
  // the server echo that follows lands as `keep`.
  const applyOrganizeResult = useCallback((newContent) => {
    if (typeof newContent !== 'string') return;
    // Drop any save still queued from before the sort. It holds PRE-sort
    // text, and letting it fire afterwards re-sends the old document over
    // the sorted one — the second half of the oscillation Walter's log
    // caught on 2026-08-24 (server size going 79 → 64 → 79 within a second).
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    pendingPatch.current = null;
    setLocalContent(newContent);
    localContentRef.current = newContent;
    setBase(newContent);
    const view = mdEditorRef.current?.view;
    if (view) {
      // CM6 owns its document, so setLocalContent alone wouldn't move it.
      const caret = Math.min(view.state.selection.main.head, newContent.length);
      dispatchDocReplace(view, newContent, caret, caret);
    }
  }, []);

  // Manual line highlights (Ctrl+H). See ListPanel for rationale; commits
  // through the same path as a keystroke. Walter 2026-06-20.
  const formatCommit = useCallback((next, selStart, selEnd) => {
    setLocalContent(next);
    const patch = { content: next };
    if (list?.seededFromTemplate) patch.seededFromTemplate = false;
    scheduleSave(patch);
    if (lockEnabled) { lock.touch(); markEditing(); }
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { try { ta.setSelectionRange(selStart, selEnd); } catch { /* noop */ } }
    });
  }, [list?.seededFromTemplate, scheduleSave, lockEnabled, lock, markEditing, textareaRef]);

  // Format-menu commit for the CM6 (live-preview) editor: dispatch the rewrite
  // straight to the view as one minimal transaction (the #132/#136 lesson —
  // the view owns the doc while focused; a localContent replace would clobber
  // it). onChange then syncs localContent + schedules the save, so the debounce
  // and reminder reconcile ride the normal path. Walter 2026-07-16.
  const cmFormatCommit = useCallback((next, selStart, selEnd) => {
    const view = mdEditorRef.current?.view;
    if (!view) return;
    dispatchDocReplace(view, next, selStart, selEnd);
    if (lockEnabled) { lock.touch(); markEditing(); }
  }, [lockEnabled, lock, markEditing]);

  const highlight = useLineHighlight({
    textareaRef,
    getContent: () => localContent,
    isReminderList: isDesignatedReminderList(userPrefs, list?.id),
    readOnly: effectiveReadOnly,
    pushToast,
    commit: formatCommit,
  });

  // Walter 2026-05-15: register flushSave with the parent so the
  // SaveIndicator's "tap to sync now" gesture can flush this panel.
  useEffect(() => {
    if (!registerPendingFlush) return undefined;
    return registerPendingFlush(flushSave);
  }, [registerPendingFlush, flushSave]);

  // Flush on tab hide, page unload, and unmount (e.g. user closes focused view).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') flushSave(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flushSave);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flushSave);
      flushSave();
    };
  }, [flushSave]);

  // ── Parse outline + date anchors ──
  const outline = useMemo(() => parseOutline(localContent), [localContent]);
  const dateAnchors = useMemo(() => parseDateAnchors(localContent), [localContent]);
  const railEntries = useMemo(
    () => mergeOutlineAndDates(outline, dateAnchors),
    [outline, dateAnchors],
  );

  // ── Auto-focus the textarea on mount ──
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // ── Jump from search or outline click ──
  // Intentionally only depending on jumpTarget — we don't want re-jumps when the user types.
  // Walter 2026-05-13: defer the jump to the next frame so the textarea
  // has its final clientHeight when scrollTop is computed. Firing the
  // jump synchronously in the effect raced with the focused-panel
  // mount transition — clientHeight was 0, the centering math
  // produced scrollTop=0, and the entry landed off-screen even though
  // the caret was at the right offset.
  useEffect(() => {
    if (!jumpTarget) return;
    const id = requestAnimationFrame(() => {
      jumpToOffset(textareaRef.current, jumpTarget.charOffset ?? 0, localContent);
      onJumpHandled?.();
    });
    return () => cancelAnimationFrame(id);
  }, [jumpTarget]);

  // Exit focused view, carrying the current cursor position back to the compact panel.
  const exitWithCursor = useCallback(() => {
    const offset = textareaRef.current?.selectionStart ?? 0;
    onExit(offset);
  }, [onExit]);

  // ── Escape or Ctrl+E to exit (unless editing the title) ──
  // Ctrl+E is a toggle — ListPanel handles expand, FocusedPanel handles collapse.
  // Esc has a two-stage exit when full-screen: first press collapses
  // out of full-screen back to the focused-overlay layout; second
  // press closes the panel entirely. Matches video-player full-screen
  // conventions — never have one button bypass two distinct states.
  useEffect(() => {
    const h = (e) => {
      if (editingTitle) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (fullScreen) {
          setFullScreen(false);
        } else {
          exitWithCursor();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        exitWithCursor();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [editingTitle, exitWithCursor, fullScreen]);

  // ── Title editing ──
  const confirmTitleEdit = useCallback(() => {
    setEditingTitle(false);
    const val = localTitle.trim() || '';
    setLocalTitle(val);
    onUpdate(list.id, { title: val });
  }, [localTitle, list.id, onUpdate]);

  const onTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); confirmTitleEdit(); }
    if (e.key === 'Escape') {
      e.stopPropagation();
      setEditingTitle(false);
      setLocalTitle(list.title || '');
    }
  }, [confirmTitleEdit, list.title]);

  const hasTitle = localTitle && localTitle.trim() !== '';
  // Walter 2026-05-11: same shared-list semantics as ListPanel —
  // title is owner-only, with a cached fallback when the owner's
  // live title is empty.
  const displayTitle = hasTitle
    ? localTitle
    : (isSharedWithMe ? (list._sharedFromCachedTitle || '') : '');
  const displayHasTitle = displayTitle.trim() !== '';
  const titleEditable = roleCanEdit && !isSharedWithMe;

  const handleOutlineClick = (header) => {
    if (inline) {
      // Mobile inline: OutlineBand handles its own click-and-jump,
      // so this branch is dead now (legacy drawer + FAB are gone).
      // Kept for safety in case OutlineRow is ever wired into a
      // new mobile path.
      jumpToOffset(textareaRef.current, header.charOffset, localContent);
    } else {
      jumpToOffset(textareaRef.current, header.charOffset, localContent);
    }
  };

  // Outer wrapper styles. Three variants:
  //   inline + !fullScreen   → in-flow panel (mobile default)
  //   !inline + !fullScreen  → centered desktop overlay (~92vw, 1100px max)
  //   fullScreen (any)       → fixed top:0 covering everything incl. app
  //                            header and sidebar, for distraction-free
  //                            note-taking. zIndex bumped well above the
  //                            normal overlay's 601 so any other modals
  //                            don't peek through.
  // Per-list color tag (Walter 2026-05-24, mirrors ListPanel). When
  // `list.color` is set, paint a 3px accent on the left edge of the
  // focused panel via an inset box-shadow so the tag is visible even
  // when viewing one list at a time.
  // Walter 2026-06-16: per-list color now paints the whole 1px frame (all
  // sides) + header + outline band, instead of a 3px left-only bar. Keeps
  // the same thickness as the default border; the colored card reads as one
  // coherent hue rather than a one-sided stripe.
  // Walter 2026-07-03: the coloured frame is a FAINT tint of the colour (not the
  // saturated hue) so it doesn't read like the saturated active-tile outline —
  // consistent with the canvas tile.
  const focusedColorValue = getListColorTint(list?.color, 0.45);
  const outerStyle = fullScreen
    ? {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100vw',
        // Walter 2026-05-09: maxWidth + overflowX on the root so any
        // unexpected child overflow (long unbreakable strings, iOS
        // Safari textarea quirks) is clipped at the viewport edge
        // rather than bleeding past it visually.
        maxWidth: '100vw',
        background: getListColorBodyBg(list?.color) || 'var(--surface-note)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 800,
        overflow: 'hidden',
        overflowX: 'hidden',
        animation: 'lists-focus-in 0.15s ease',
        // Fullscreen is edge-to-edge (no frame) — the color cue rides on
        // the tinted header + outline band instead.
        boxShadow: undefined,
      }
    : (inline
      ? {
          flex: 1, minWidth: 0, minHeight: 0,
          background: getListColorBodyBg(list?.color) || 'var(--surface-note)',
          // Whole 1px frame in the list color (was a 3px left bar).
          border: `1px solid ${focusedColorValue || 'var(--border)'}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowX: 'hidden',
          position: 'relative',
        }
      : {
          position: 'fixed',
          top: 'calc(var(--header-h) + 16px)',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(92vw, 1100px)',
          background: getListColorBodyBg(list?.color) || 'var(--surface-note)',
          // Whole frame in the list color when set, else the default
          // bright border.
          border: `1px solid ${focusedColorValue || 'var(--border-bright)'}`,
          borderRadius: 'var(--radius)',
          boxShadow: [
            '0 0 0 1px var(--accent-dim)',
            'var(--shadow-focused-panel)',
          ].filter(Boolean).join(', '),
          display: 'flex',
          flexDirection: 'column',
          zIndex: 601,
          overflow: 'hidden',
          animation: 'lists-focus-in 0.15s ease',
        });

  return (
    <>
      {/* Backdrop — desktop only. Inline (mobile) has no dimmed backdrop;
          the nav strip next to it is the only other thing on screen.
          Clicking the backdrop exits the focused view — standard "click
          away to dismiss" affordance. Uses exitWithCursor so the caret
          position carries back to the compact panel's textarea. */}
      {!inline && (
        <div
          onMouseDown={exitWithCursor}
          style={{
            position: 'fixed',
            top: 'var(--header-h)',
            left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 600,
            animation: 'lists-fadein 0.12s ease',
            cursor: 'pointer',
          }}
        />
      )}

      {/* Focused / inline panel */}
      {/* data-find-scope: while a list is open like this, Ctrl+F searches only
          what's inside it. The board's tiles stay MOUNTED behind the overlay,
          so without this the find bar counts hits in lists the user can't read
          — Walter 2026-08-24 searched "test" in an open list and got 4/4 for a
          word appearing once on screen. See FindBar.collectMatches. */}
      <div style={outerStyle} data-find-scope="1">
        {/* ── Header ── */}
        <div ref={headerBarRef} style={{
          display: 'flex', alignItems: 'center',
          padding: '0 10px 0 14px', height: '40px',
          flexShrink: 0,
          borderBottom: '1px solid var(--border)',
          // Walter 2026-06-13: per-list color washes the focused header too,
          // matching the canvas tile.
          background: getListColorHeaderBg(list?.color) || 'var(--surface2)', gap: '6px',
        }}>
          {/* Designated reminder list marker — small bell BEFORE the title.
              Mirrors the desktop ListPanel header so mobile users can see at
              a glance which list is the "every paragraph becomes a reminder"
              one without diving into Settings. */}
          {isDesignatedReminderList(userPrefs, list.id) && (
            <span
              title="this list is being used for reminders — every line becomes one, and lines right under it are its notes"
              style={{
                flex: 'none',
                color: 'var(--accent)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                marginRight: '2px',
              }}
            >
              🔔
            </span>
          )}
          {isDesignatedLogList(userPrefs, list.id) && (
            <span
              title="this list is a log — every new paragraph auto-stamps with today's date"
              style={{
                flex: 'none',
                color: 'var(--accent)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))',
                fontWeight: 700,
                marginRight: '2px',
                lineHeight: 1,
              }}
            >
              @
            </span>
          )}
          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={localTitle}
              onChange={e => setLocalTitle(e.target.value)}
              onBlur={confirmTitleEdit}
              onKeyDown={onTitleKeyDown}
              placeholder="list title..."
              autoFocus
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: 'var(--accent)', fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600,
                padding: '2px 4px', borderRadius: '2px',
                borderBottom: '1px solid var(--accent-dim)', minWidth: 0,
              }}
              spellCheck={false}
            />
          ) : (
            <span
              // Layout cell: flex:1 + truncation, no click handler — clicking
              // the empty area past the text doesn't enter rename (mirrors the
              // canvas tile). The inner span is the actual click target.
              style={{
                flex: 1, fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600,
                // Walter 2026-07-03: a coloured list paints its title in its own
                // colour here too (matching the sidebar name), on top of the
                // header's colour wash.
                color: displayHasTitle ? (getListColorTextValue(list?.color) || 'var(--text)') : 'var(--text-dimmer)',
                fontStyle: displayHasTitle ? 'normal' : 'italic',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {/* Walter 2026-07-04: single-click to rename, but only on the
                  letters (inline) — not the whole cell. */}
              <span
                onClick={titleEditable ? () => setEditingTitle(true) : undefined}
                onMouseDown={titleEditable ? (e) => e.stopPropagation() : undefined}
                title={
                  !titleEditable && isSharedWithMe ? "owner controls this list's title"
                    : titleEditable ? (displayHasTitle ? 'click to rename' : 'click to name')
                    : undefined
                }
                style={{ cursor: titleEditable ? 'text' : 'default' }}
              >
                {displayHasTitle ? displayTitle : '(empty)'}
              </span>
            </span>
          )}
          {/* Walter slide 1 (2026-05-05): designated reminder lists
              get a clickable count chip in the focused-view header
              ("N active, M here, K elsewhere") that opens the
              structured reminder view (agenda panel). Mirrors the
              info from the canvas tile's reminder band but lives in
              the header here so the focused view's clean prose
              column isn't broken up by a band underneath. */}
          {isDesignatedReminderList(userPrefs, list.id) && Array.isArray(reminders) && onOpenAgenda && (() => {
            const active = reminders.filter(r => r.status !== 'done');
            if (active.length === 0) return null;
            const here = active.filter(r => r.sourceListId === list.id).length;
            const elsewhere = active.length - here;
            return (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenAgenda(); }}
                title="open all reminders"
                style={{
                  flexShrink: 0,
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  padding: '2px 8px',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  color: 'var(--text-dim)',
                  cursor: 'pointer',
                  transition: 'color 0.12s, border-color 0.12s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = 'var(--accent)';
                  e.currentTarget.style.borderColor = 'var(--accent-dim)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = 'var(--text-dim)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {active.length} active, {here} here, {elsewhere} elsewhere
              </button>
            );
          })()}
          {/* Walter 2026-06-11: organise/sort button — mirrors the canvas
              tile (ListPanel). Sorts the reminder list's paragraphs into
              dated sections via the Accept/Undo preview flow (band below).
              Shown only for an editor on a list that actually sources
              reminders, and hidden while a pass is already pending. */}
          {roleCanEdit && Array.isArray(reminders) && reminders.some(r => r.sourceListId === list.id) && onTriggerOrganize && (
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={async () => {
                // Walter 2026-06-16: capture pending lines first (so sorting
                // right after typing isn't acting on an uncaptured line),
                // then flush, then organize — see ListPanel for the full
                // rationale, including why the text comes from the editor.
                // (0) TREAT SORT AS AN IMPLICIT ENTER. Walter 2026-08-24: after
                //     the write race was closed, the one way left to lose a line
                //     was to type it and hit sort WITHOUT pressing Enter. Capture
                //     ran on `localContent` — React state, which lags the last
                //     keystroke, and in the CM6 editor lags the document by a
                //     render — so the in-progress line wasn't there to capture,
                //     wasn't in the pending save, and organize then read a server
                //     copy that had never heard of it. Take the text straight
                //     from the editor instead, and make it the pending write.
                const fresh = mdEditorRef.current?.view
                  ? mdEditorRef.current.view.state.doc.toString()
                  : localContent;
                if (fresh !== localContent) {
                  setLocalContent(fresh);
                  localContentRef.current = fresh;
                  scheduleSave({ content: fresh });
                }
                // (1) CAPTURE any just-typed-but-uncaptured lines and AWAIT it,
                //     so the line you just typed is a real bell-line/reminder
                //     before organize reads it. Idempotent.
                try { await reminderCapture.captureAllUncaptured?.(fresh); } catch { /* non-fatal */ }
                // ...and let any debounced reminder reconcile land, so organize sorts
                // against docs that match the text it is about to read.
                try { await reminderCapture.flushReminderReconcile?.(); } catch { /* non-fatal */ }
                try { await flushSave(); } catch { /* non-fatal */ }
                // (3) Apply the sorted text to THIS buffer rather than waiting
                //     for it to arrive as a remote change — see
                //     applyOrganizeResult.
                const res = await onTriggerOrganize();
                if (res?.newContent != null) applyOrganizeResult(res.newContent);
              }}
              style={{
                width: '22px', height: '22px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none',
                color: 'var(--text-dimmer)', borderRadius: '2px',
                padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
              title="organise reminders into sections"
              aria-label="organise reminders"
            >
              {/* Same sort-lines glyph as ListPanel for visual parity. */}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1.5 2.5h7 M1.5 5.5h5 M1.5 8.5h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <path d="M9.5 3v6 M8 7.5l1.5 1.5 1.5-1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {/* Shared-state badge — same as in tile chrome but slightly
              larger for the focused view's header. Only renders when
              the list has share state. */}
          <ShareBadge
            editors={list.editors}
            viewers={list.viewers}
            shareToken={list.shareToken}
            boardEditors={boardEditors}
            boardViewers={boardViewers}
            size={12}
            onClick={() => onPeekShare?.(list)}
          />
          {/* Walter 2026-05-16: previous-versions icon. Walter
              2026-05-17: tied to the new conflictPendingReview flag,
              with the legacy hasConflictMarker check as fallback so
              existing markered lists still surface the affordance. */}
          {isConflictBandVisible({ flag: list?.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={() => setShowHistory(true)}
              style={{
                width: '22px', height: '22px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none',
                color: 'var(--text-dimmer)', borderRadius: '2px',
                padding: 0, flexShrink: 0, transition: 'color 0.15s',
                cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dimmer)'}
              title={`${list.historyCount} previous version${list.historyCount === 1 ? '' : 's'} — click to view`}
              aria-label="previous versions"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 5.5a4 4 0 1 1 1.2 2.85"
                  stroke="currentColor" strokeWidth="1.3"
                  strokeLinecap="round" fill="none"
                />
                <path
                  d="M2 2.5v3h3"
                  stroke="currentColor" strokeWidth="1.3"
                  strokeLinecap="round" strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}

          {/* (Char count removed in the new design — clutter that the
              header didn't need. Title alone reads cleaner.) */}

          {/* `⋯` menu — rendered when any action is available. Contents depend on
              mode: desktop only gets the move-to-board list; mobile (inline) also
              gets archive/unarchive and delete since there's no canvas tile to
              reach those controls from. Sits before the outline/close buttons. */}
          {showOverflowMenu && (
            <>
              <button
                ref={moveMenuRef}
                onClick={() => setMoveMenuOpen(v => !v)}
                title="more"
                style={{
                  width: '26px', height: '26px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: moveMenuOpen ? 'var(--surface)' : 'none',
                  border: 'none',
                  color: moveMenuOpen ? 'var(--accent)' : 'var(--text-dim)',
                  borderRadius: '2px', padding: 0,
                  transition: 'color 0.15s, background 0.15s',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onMouseEnter={e => {
                  if (!moveMenuOpen) e.currentTarget.style.color = 'var(--accent)';
                }}
                onMouseLeave={e => {
                  if (!moveMenuOpen) e.currentTarget.style.color = 'var(--text-dim)';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="2.5" r="1" fill="currentColor"/>
                  <circle cx="6" cy="6" r="1" fill="currentColor"/>
                  <circle cx="6" cy="9.5" r="1" fill="currentColor"/>
                </svg>
              </button>
              <PortalMenu
                open={moveMenuOpen}
                triggerRef={moveMenuRef}
                onClose={() => setMoveMenuOpen(false)}
                minWidth={180}
                maxHeight={320}
                // Walter 2026-05-25 (Bug #1 — round-2 tester report):
                // PortalMenu's default zIndex is 60, but FocusedPanel
                // renders at zIndex 800 (fullScreen) or 601 (centered
                // overlay). The menu was opening but rendering BEHIND
                // the panel, so clicking the ⋯ button looked like it
                // did nothing in fullScreen mode. Raise above both.
                zIndex={900}
              >
                  {menuMode === 'main' ? (
                    <>
                      {/* Rename — owner-side only. Walter 2026-05-11
                          extended the gate to also block invitees on
                          shared lists (titleEditable = roleCanEdit &&
                          !isSharedWithMe). Title is owner-controlled;
                          content stays editable for board / list
                          editors via the textarea. */}
                      {titleEditable && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            setEditingTitle(true);
                            requestAnimationFrame(() => titleInputRef.current?.focus());
                          }}
                        >
                          rename
                        </MenuRow>
                      )}
                      {showArchiveItem && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            onArchive(list.id);
                            // Close focused view after archive — the list is
                            // now in the archive section and the user is
                            // clearly done.
                            exitWithCursor();
                          }}
                        >
                          archive
                        </MenuRow>
                      )}
                      {showUnarchiveItem && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            onUnarchive(list.id);
                          }}
                        >
                          unarchive
                        </MenuRow>
                      )}
                      {/* Designate / un-designate as reminder list.
                          Walter 2026-05-06 round 8: reminders always
                          on, so no feature-flag gate. Mirrors the
                          desktop ListPanel header menu so mobile users
                          (where this view is the editor) get the same
                          per-list action surface. */}
                      {updateUserPrefs && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            const ids = Array.isArray(userPrefs.designatedReminderListIds)
                              ? userPrefs.designatedReminderListIds
                              : [];
                            const currentlyDesignated = isDesignatedReminderList(userPrefs, list.id);
                            const next = currentlyDesignated
                              ? ids.filter(id => id !== list.id)
                              : Array.from(new Set([...ids, list.id]));
                            updateUserPrefs({
                              designatedReminderListIds: next,
                              designatedReminderListId: null,
                              designatedReminderBoardId: null,
                            });
                          }}
                        >
                          {isDesignatedReminderList(userPrefs, list.id)
                            ? 'stop using for reminders'
                            : 'use this list for reminders'}
                        </MenuRow>
                      )}
                      {/* Log-list designation. Mirrors ListPanel's
                          item — auto-stamps `@today` on every new
                          paragraph; lines surface in the log view. */}
                      {updateUserPrefs && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            const ids = Array.isArray(userPrefs.designatedLogListIds)
                              ? userPrefs.designatedLogListIds
                              : [];
                            const currentlyLog = isDesignatedLogList(userPrefs, list.id);
                            const next = currentlyLog
                              ? ids.filter(id => id !== list.id)
                              : Array.from(new Set([...ids, list.id]));
                            updateUserPrefs({ designatedLogListIds: next });
                            // Walter 2026-06-16: no demo-content seed — an empty
                            // log list shows the examples as a placeholder
                            // (getListEmptyHint), never @date-stampable content.
                          }}
                        >
                          {isDesignatedLogList(userPrefs, list.id)
                            ? 'stop using as log'
                            : 'use this list as log'}
                        </MenuRow>
                      )}
                      {/* Share this list \u2014 Phase 2. Alpha-gated. */}
                      {onShareList && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            onShareList(list);
                          }}
                        >
                          share list
                        </MenuRow>
                      )}
                      {/* "place in board\u2026" \u2014 Walter 2026-05-11.
                          Visible when the list is shared with the
                          current user; sets a sharedListPlacements
                          doc so the shared list renders alongside
                          their own lists on the chosen board. */}
                      {isSharedWithMe && onPlaceList && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            onPlaceList(list);
                          }}
                        >
                          place in board{'\u2026'}
                        </MenuRow>
                      )}
                      {showMoveSection && (
                        <MenuRow onClick={() => setMenuMode('move')}>
                          move to {'\u2192'}
                        </MenuRow>
                      )}
                      {/* Colour \u2014 a sub-view (not a fly-out) so it works on
                          touch, matching the sidebar/tile colour picker. Walter
                          2026-07-02: the mobile tile menu was missing colours. */}
                      <MenuRow onClick={() => setMenuMode('color')}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                          marginRight: 8, verticalAlign: 'middle',
                          background: getListColorValue(list?.color) || 'transparent',
                          border: getListColorValue(list?.color) ? 'none' : '1px solid var(--border-bright)',
                        }} />
                        colour {'\u2192'}
                      </MenuRow>
                      {/* Walter 2026-06-13: always-available access to this
                          list's version history. Previously the only entry
                          point was the header clock icon, which is gated on
                          an ACTIVE conflict (conflictPendingReview) \u2014 so once
                          a conflict was resolved the snapshots became
                          unreachable, even though they persist in the
                          lists/{id}/history subcollection. Shown whenever the
                          list has any snapshots so overwritten versions stay
                          recoverable. */}
                      {list?.historyCount > 0 && (
                        <MenuRow
                          onClick={() => {
                            setMoveMenuOpen(false);
                            setShowHistory(true);
                          }}
                        >
                          previous versions
                        </MenuRow>
                      )}
                      {showDeleteItem && (
                        <MenuRow
                          danger
                          onClick={() => {
                            setMoveMenuOpen(false);
                            if (window.confirm('Move this list to trash? You can restore it within 30 days from the trash menu.')) {
                              onDelete(list.id);
                              // The list disappears from props; MobileBoard's
                              // effect will clear activeListId, but calling
                              // exit here is harmless and makes desktop
                              // behaviour identical.
                              exitWithCursor();
                            }
                          }}
                        >
                          delete
                        </MenuRow>
                      )}
                    </>
                  ) : menuMode === 'color' ? (
                    <>
                      <MenuRow dim onClick={() => setMenuMode('main')}>
                        {'\u2190'} back
                      </MenuRow>
                      <MenuRow onClick={() => { setMoveMenuOpen(false); setMenuMode('main'); onUpdate?.(list.id, { color: null }); }}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                          marginRight: 8, verticalAlign: 'middle',
                          border: '1px solid var(--border-bright)',
                        }} />
                        none
                      </MenuRow>
                      {LIST_COLORS.map(c => (
                        <MenuRow
                          key={c.id}
                          onClick={() => { setMoveMenuOpen(false); setMenuMode('main'); onUpdate?.(list.id, { color: c.id }); }}
                        >
                          <span style={{
                            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                            marginRight: 8, verticalAlign: 'middle', background: c.value,
                          }} />
                          {c.label}
                        </MenuRow>
                      ))}
                    </>
                  ) : (
                    <>
                      <MenuRow dim onClick={() => setMenuMode('main')}>
                        {'\u2190'} back
                      </MenuRow>
                      {otherBoards.map(b => (
                        <MenuRow
                          key={b.id}
                          onClick={() => {
                            setMoveMenuOpen(false);
                            setMenuMode('main');
                            onMoveList(list.id, b.id);
                          }}
                        >
                          {b.name}
                        </MenuRow>
                      ))}
                    </>
                  )}
              </PortalMenu>
            </>
          )}

          {/* Mobile outline-toggle button removed (round 5 cleanup):
              OutlineBand below the header is now the single affordance
              for jumping to a section, on both surfaces. The legacy
              drawer (kept temporarily) is also gone. Walter's E. */}

          {/* Full-screen toggle — visible in non-fullscreen state
              on BOTH desktop and mobile (Walter reversed II.6 in
              round 5 — wants the focused-view square back on mobile
              alongside the X close). Hidden in fullscreen itself —
              the close button takes the user back out (Walter's
              II.7). */}
          {!fullScreen && (
            <button
              onClick={() => setFullScreen(true)}
              title="full-screen note (hide all chrome)"
              style={{
                width: '26px', height: '26px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none',
                color: 'var(--text-dim)',
                borderRadius: '2px',
                padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                {/* Standard full-screen square — outward corner brackets. Walter
                    2026-07-11: keep the full-screen control's own standard symbol
                    (the diagonal arrows live on the tile-expand button). */}
                <path
                  d="M1 4V1H4 M11 4V1H8 M11 8V11H8 M1 8V11H4"
                  stroke="currentColor" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Close / exit button. Action depends on context:
                fullScreen → exit fullscreen back to focused overlay
                otherwise  → exit focused view back to canvas
              Icon: ↙ back-arrow in fullscreen (Walter's II.7 — reads
              as "go back" / "make smaller"), corner-brackets in
              normal focused mode (existing affordance). */}
          <button
            onClick={() => {
              if (fullScreen) setFullScreen(false);
              else exitWithCursor();
            }}
            title={fullScreen ? 'exit full-screen (esc)' : `exit focused view (esc or ${SHORTCUT.expand})`}
            style={{
              width: '26px', height: '26px', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none',
              color: 'var(--text-dim)', borderRadius: '2px',
              padding: 0, flexShrink: 0, transition: 'color 0.15s', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              {fullScreen ? (
                /* Standard full-screen-EXIT square — inward corner brackets
                   (Walter 2026-07-11: the full-screen toggle keeps its standard
                   square symbol, not the diagonal arrows). */
                <path
                  d="M4 1V4H1 M8 1V4H11 M8 11V8H11 M4 11V8H1"
                  stroke="currentColor" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              ) : (
                /* Diagonal inward "make smaller" arrows — closing the focused
                   overlay shrinks the note back to its tile, so it mirrors the
                   tile's outward-arrow expand (Walter 2026-07-12). The square
                   brackets stay reserved for the full-screen ⇄ focused pair. */
                <path
                  d="M11 1 L7 5 M7 5 L7 2 M7 5 L10 5 M1 11 L5 7 M5 7 L5 10 M5 7 L2 7"
                  stroke="currentColor" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Shared-from band — Walter 2026-05-11. Same component as
            ListPanel's. Renders on both inline (mobile) and overlay
            (desktop) variants so the owner badge is visible wherever
            the user opens a shared list. */}
        {/* Walter 2026-05-26 band merge — see ListPanel for the full
            rationale. When the list is shared AND has outside
            reminders, fold the muted note into SharedFromBand as a
            sub-line so we ship one band, not two. */}
        {(() => {
          const outsideCount = countOutsideReminders(localContent, list?.id, reminders);
          if (isSharedWithMe) {
            return (
              <SharedFromBand
                ownerName={list._sharedFromOwnerName}
                role={roleCanEdit ? 'editor' : 'viewer'}
                mutedReminderNote={outsideCount > 0}
              />
            );
          }
          // Walter 2026-06-01: see ListPanel — only show the "silent
          // for you" band to the owner when the list is genuinely shared
          // with others. On a private list any outsideCount is hash
          // drift (advanced recurring / edited / multi-line reminders),
          // not someone else's reminder. Suppress the false positive.
          const listSharedWithOthers = !!(
            (Array.isArray(list?.editors) && list.editors.length > 0) ||
            (Array.isArray(list?.viewers) && list.viewers.length > 0) ||
            list?.shareToken
          );
          if (outsideCount > 0 && listSharedWithOthers) {
            return (
              <MutedRemindersBand
                content={localContent}
                listId={list?.id}
                reminders={reminders}
              />
            );
          }
          return null;
        })()}

        {/* Walter's II.8 — same OutlineBand as ListPanel for
            consistency. Renders below header on mobile (inline)
            so the headers affordance matches the canvas tile.
            Desktop FocusedPanel keeps its left-column outline
            (different surface — column lives alongside the textarea
            and is the focus-mode specialty). */}
        {inline && (
          <OutlineBand
            outline={outline}
            dateAnchors={dateAnchors}
            textareaRef={textareaRef}
            content={localContent}
            colorBg={getListColorHeaderBg(list?.color)}
          />
        )}

        {/* Legacy mobile outline drawer removed (round 5 cleanup):
            OutlineBand above is the single affordance now. */}

        {/* ── Body: outline (desktop) + textarea ── */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left: outline rail — shows both `#`-headers and `@<date>`
              anchors in document order. Hidden in inline (mobile)
              mode; mobile uses the OutlineBand below the header
              instead. */}
          {!inline && (
            <div style={{
              width: 260,
              flexShrink: 0,
              borderRight: '1px solid var(--border)',
              background: 'var(--bg)',
              overflowY: 'auto',
              padding: '10px 0',
            }}>
              {railEntries.length === 0 ? (
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: '11px',
                  color: 'var(--text-dimmer)', fontStyle: 'italic',
                  padding: '10px 16px', lineHeight: 1.6,
                }}>
                  no anchors yet — start a line with{' '}
                  <span style={{ fontStyle: 'normal', color: 'var(--text-dim)' }}>#&nbsp;</span>
                  {' '}for a section or{' '}
                  <span style={{ fontStyle: 'normal', color: 'var(--text-dim)' }}>@today&nbsp;</span>
                  {' '}for a dated entry
                </div>
              ) : railEntries.map((entry, i) => (
                <RailRow
                  key={i}
                  entry={entry}
                  onClick={() => jumpToOffset(textareaRef.current, entry.charOffset, localContent)}
                />
              ))}
            </div>
          )}

          {/* Right: body */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Conflict band — see ListPanel for full rationale.
                Walter 2026-05-17: keys off the new
                conflictPendingReview flag too, and flex-wraps so the
                button stacks under the text on narrow viewports. */}
            {isConflictBandVisible({ flag: list?.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
              <div style={{
                padding: '8px 24px',
                borderBottom: '1px solid var(--accent-dim)',
                background: 'var(--surface2)',
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
                flexShrink: 0,
              }}>
                <span style={{ color: 'var(--accent)', flexShrink: 0 }}>🔀</span>
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  this list was edited in two places at once — review and resolve
                </span>
                <button
                  onClick={() => setShowHistory(true)}
                  disabled={effectiveReadOnly}
                  title="open side-by-side view to choose how to resolve"
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--bg)',
                    border: '1px solid var(--accent)',
                    borderRadius: 'var(--radius)',
                    padding: '2px 10px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    cursor: effectiveReadOnly ? 'not-allowed' : 'pointer',
                    opacity: effectiveReadOnly ? 0.5 : 1,
                    fontWeight: 600,
                  }}
                >
                  resolve…
                </button>
              </div>
            )}

            {/* Offline banner — see ListPanel for full rationale.
                Wins over the viewer/lock badges below; underlying
                state is moot until back online. */}
            {offlineLockedShared && (
              <div style={{
                padding: '8px 24px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface2)',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
                flexShrink: 0,
              }}>
                <span style={{ color: 'var(--accent)' }}>📶</span>
                <span style={{ flex: 1 }}>
                  you're offline — shared lists are read-only until you reconnect
                </span>
              </div>
            )}
            {/* Viewer banner — shown when role doesn't permit writes
                (board-/list-level viewer). Same surface idea as in
                ListPanel: replace the "type and silently fail" UX
                with explicit read-only feedback. */}
            {!offlineLockedShared && !roleCanEdit && lockEnabled && (
              <div style={{
                padding: '8px 24px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface2)',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
                flexShrink: 0,
              }}>
                <span style={{ color: 'var(--accent)' }}>👁</span>
                <span style={{ flex: 1 }}>
                  view-only — only editors can change this list
                </span>
              </div>
            )}
            {/* Self-editing badge — see ListPanel for full rationale.
                Shown when YOU hold the lock; gives you a manual
                "done, release" so a collaborator can take over. */}
            {/* Walter 2026-05-16: shared-only band — solo multi-device
                lock-hold is implementation detail. */}
            {!offlineLockedShared && roleCanEdit && hasSharedCollaborator && lock.heldByMe && (
              <div style={{
                padding: '4px 24px',
                borderBottom: '1px solid var(--accent-dim)',
                background: 'var(--surface2)',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
                flexShrink: 0,
              }}>
                <span style={{ color: 'var(--accent)' }}>✏️</span>
                <span style={{ flex: 1 }}>editing shared list</span>
                <button
                  onClick={() => lock.release()}
                  title="release the lock so someone else can edit"
                  style={{
                    background: 'transparent',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent-dim)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 10px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    cursor: 'pointer',
                  }}
                >
                  done, release
                </button>
              </div>
            )}

            {/* Lock badge — same shape as ListPanel's. Sits above the
                textarea in this layout (below in ListPanel — different
                vertical structure). Skipped for viewers (the banner
                above is the right surface for them) and skipped
                offline (lock state could be stale). */}
            {!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe && (
              <div style={{
                padding: '4px 24px',
                borderBottom: '1px solid var(--accent-dim)',
                background: 'var(--surface2)',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
                flexShrink: 0,
              }}>
                <span style={{ color: 'var(--accent)' }}>🔒</span>
                <span style={{ flex: 1 }}>
                  {/* Walter 2026-05-16: same per-device branch as ListPanel. */}
                  {lock.heldBy.isOtherDevice
                    ? `your other device is ${lock.heldBy.activelyTyping ? 'typing' : 'editing'}`
                    : `${lock.heldBy.name} ${lock.heldBy.activelyTyping ? 'is typing' : 'is editing'}`}
                </span>
                <button
                  onClick={async () => {
                    if (lock.heldBy?.activelyTyping) return;
                    await lock.acquire({ force: true });
                    // Walter 2026-05-16: same explicit-content-resync
                    // pattern as ListPanel — make sure take-over
                    // shows what the other editor actually wrote,
                    // even if the snapshot is mid-flight.
                    setLocalContent(list.content || '');
                    textareaRef.current?.focus();
                  }}
                  disabled={lock.heldBy?.activelyTyping}
                  className={takeoverFlash && !lock.heldBy?.activelyTyping ? 'takeover-flash-pulse' : undefined}
                  title={lock.heldBy?.activelyTyping
                    ? 'wait until they pause typing'
                    : 'take over editing'}
                  style={{
                    background: 'transparent',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent-dim)',
                    borderRadius: 'var(--radius)',
                    padding: '2px 8px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    cursor: 'pointer',
                  }}
                >
                  take over
                </button>
              </div>
            )}
            {/* Demo-content band — Walter 2026-05-26 v2. Mirrors the
                same affordance in ListPanel; see that file for full
                rationale + behavior matrix (General Tips → "keep but
                close"; other demo lists → "keep" + "✕ clear"). */}
            {/* Walter 2026-05-28: same fix as ListPanel — hide the band
                once localContent diverges from the saved content. The
                ✕ clear button wipes the whole local textarea, so a
                user mid-typing who clicks it would lose what they just
                typed. Treating "user has typed" as an implicit keep
                avoids that. */}
            {list?.seededFromTemplate && !effectiveReadOnly && localContent === (list?.content || '') && (() => {
              const isGeneralTips = (list?.title || '').trim().toLowerCase() === 'general tips';
              const btnStyle = {
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '3px 10px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                fontWeight: 500,
                color: 'var(--text-dim)',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'color 0.12s, border-color 0.12s',
              };
              const onEnter = (e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--accent-dim)'; };
              const onLeave = (e) => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.borderColor = 'var(--border)'; };
              return (
                <div
                  style={{
                    padding: '7px 12px',
                    borderTop: '1px solid var(--border)',
                    background: 'var(--surface2)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    color: 'var(--text-dim)',
                    display: 'flex',
                    alignItems: 'center',
                    // Walter 2026-06-07: wrap to two lines on narrow tiles
                    // rather than truncating the label to "exampl…".
                    flexWrap: 'wrap',
                    rowGap: '6px',
                    columnGap: '8px',
                    userSelect: 'none',
                    flexShrink: 0,
                  }}
                  title="this content was seeded by the demo template — pick how to handle it, or just start typing"
                >
                  <span style={{
                    flex: '1 1 130px',
                    minWidth: '130px',
                    whiteSpace: 'normal',
                    fontStyle: 'italic',
                  }}>
                    example content + tips
                  </span>
                  {isGeneralTips ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        // Walter 2026-05-26 — see ListPanel for the
                        // flush rationale. One-shot dismiss action
                        // should feel instant; schedule + flush
                        // bypasses the 4-second debounce.
                        scheduleSave({ state: 'closed', seededFromTemplate: false });
                        flushSave();
                      }}
                      style={btnStyle}
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                      title="close this tile (tucks it into the sidebar) — you can re-open it any time to read the tips"
                    >
                      keep but close
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => {
                          scheduleSave({ seededFromTemplate: false });
                          flushSave();
                        }}
                        style={btnStyle}
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}
                        title="keep this example content as-is — just dismiss the band"
                      >
                        keep
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => {
                          setLocalContent('');
                          scheduleSave({ content: '', seededFromTemplate: false });
                          flushSave();
                        }}
                        style={btnStyle}
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}
                        title="wipe the example content so you can start fresh"
                      >
                        ✕ clear
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
            {/* Walter 2026-05-28 (#8b): guest-reminder alert. Same
                rationale as the ListPanel inline insertion — see
                GuestReminderBand for context. */}
            {isGuest && isDesignatedReminderList(userPrefs, list?.id) && (
              <GuestReminderBand onSignIn={onRequestSignIn} />
            )}
            {/* textarea over a syntax backdrop — see ListPanel for rationale. */}
            <div style={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex' }}>
            {useLivePreview ? (
              /* Experimental CodeMirror-6 live-preview editor (Walter 2026-07-12).
                 Same save path as the textarea; reminder creation happens on blur
                 via captureAllUncaptured (CM6 has no textarea keydown/blur event).
                 The Format menu drives this editor too (Walter 2026-07-16):
                 onFocus registers a cmRef target whose commit dispatches to the
                 view. See editorFlag.js. */
              <div style={{
                flex: 1, minWidth: 0, minHeight: 0, width: '100%', overflow: 'hidden',
                // Padding moved INTO the editor content (--md-editor-pad) so
                // the CM6 scrollbar hugs the panel edge like the textarea's
                // did (Walter 2026-07-19; see ListPanel mirror).
                padding: 0,
                '--md-editor-pad': (inline || fullScreen) ? '14px 14px' : '16px 24px',
                boxSizing: 'border-box',
                fontSize: 'calc(var(--note-font-size, 12px) + 1px)',
                lineHeight: 'var(--note-line-height, 1.5)',
                opacity: effectiveReadOnly ? 0.6 : 1,
              }}>
                <MarkdownEditor
                  ref={mdEditorRef}
                  listId={list.id}
                  value={localContent}
                  readOnly={effectiveReadOnly}
                  // Desktop only: the textarea path never auto-focused, and on
                  // a phone auto-focus pops the keyboard on every tile open.
                  // Walter 2026-07-17 (mobile un-gate).
                  autoFocus={typeof window === 'undefined' || window.innerWidth > 768}
                  captureLine={effectiveReadOnly ? undefined : reminderCapture.captureLineForCM6}
                  onChange={(raw, meta) => {
                    if (effectiveReadOnly) return;
                    // #239 (Walter's rule): this window didn't originate the
                    // change — the cloud did, and our own sync effect already
                    // put it in `localContent`. Applying it to the screen is
                    // all that's wanted; saving it back would make a window
                    // nobody touched a second writer. Reminder capture stays
                    // out too: the window where it was typed owns that.
                    if (meta && meta.remote) return;
                    const { content: next, blocked, justCrossedSoft } = applyListSizeGuard(localContent, raw);
                    if (blocked && pushToast) {
                      pushToast('at the 950k character limit — please archive and start fresh', null, { ttlMs: 5000 });
                    } else if (justCrossedSoft && pushToast) {
                      pushToast('list is getting long — consider archiving and starting fresh', null, { ttlMs: 5000 });
                    }
                    reminderCapture.onContentChanged?.(localContent, next);
                    setLocalContent(next);
                    const patch = { content: next };
                    if (list?.seededFromTemplate) patch.seededFromTemplate = false;
                    scheduleSave(patch);
                    if (lockEnabled) lock.touch();
                    if (lockEnabled) markEditing();
                  }}
                  onFocus={async () => {
                    // Register as the global Format menu's target (Walter
                    // 2026-07-16 — the menu now drives CM6 too). No
                    // notifyContentChange: the CM6 onChange above already runs
                    // the reminder reconciler; wiring both would double-fire.
                    setFormatTarget({
                      listId: list.id,
                      cmRef: mdEditorRef,
                      commit: cmFormatCommit,
                      isReminderList: isDesignatedReminderList(userPrefs, list?.id),
                      readOnly: effectiveReadOnly,
                      pushToast,
                    });
                    if (!lockEnabled) return;
                    const snapshotAtFocus = list.content || '';
                    const { acquired, contended } = await lock.acquire();
                    if (acquired) return;
                    if (!contended) return;
                    setLocalContent(snapshotAtFocus);
                    if (pendingPatch.current && pendingPatch.current.content !== undefined) pendingPatch.current = null;
                    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
                    if (pushToast) pushToast('another device just took the lock — your last keystrokes weren\'t saved', null, { ttlMs: 4500 });
                  }}
                  onBlur={() => {
                    blurFormatTarget(list.id); // greys the global Format menu
                    if (!effectiveReadOnly) {
                      const cur = mdEditorRef.current?.getValue?.() ?? localContent;
                      reminderCapture.handleBlur?.({ target: { value: cur } });
                      // Don't strand a debounced reminder reconcile on the way out.
                      reminderCapture.flushReminderReconcile?.();
                    }
                    flushSave();
                    if (lockEnabled && lock.heldByMe) lock.release();
                  }}
                  // Folding the phone keyboard doesn't blur — run the same
                  // capture pass so "type a reminder, close the keyboard"
                  // registers it (Walter 2026-07-17). Idempotent; the editor
                  // stays focused and the lock stays held.
                  onKeyboardHide={() => {
                    if (effectiveReadOnly) return;
                    const cur = mdEditorRef.current?.getValue?.() ?? localContent;
                    reminderCapture.handleBlur?.({ target: { value: cur } });
                  }}
                />
              </div>
            ) : (<>
            <SyntaxBackdrop content={localContent} textareaRef={textareaRef} isReminderList={isDesignatedReminderList(userPrefs, list?.id)} />
            <textarea
              ref={textareaRef}
              value={localContent}
              data-find-target="1"
              // Walter 2026-06-16: reminder/log how-it-works examples as an
              // empty-state placeholder, never seeded content (see ListPanel).
              placeholder={getListEmptyHint(userPrefs, list.id)}
              readOnly={effectiveReadOnly}
              onChange={e => {
                // Defense-in-depth: refuse writes if read-only.
                if (effectiveReadOnly) return;
                const raw = e.target.value;
                // Size guard: cap content at LIST_CHAR_HARD_LIMIT.
                // Soft-warn toast fires the first time the user
                // crosses the soft threshold.
                const { content: next, blocked, justCrossedSoft } =
                  applyListSizeGuard(localContent, raw);
                if (blocked && pushToast) {
                  pushToast('at the 950k character limit — please archive and start fresh', null, { ttlMs: 5000 });
                } else if (justCrossedSoft && pushToast) {
                  pushToast('list is getting long — consider archiving and starting fresh', null, { ttlMs: 5000 });
                }
                reminderCapture.onContentChanged?.(localContent, next);
                setLocalContent(next);
                // Walter 2026-05-26 — see ListPanel for the full
                // rationale. Auto-clear demo-content flag on first edit.
                const patch = { content: next };
                if (list?.seededFromTemplate) patch.seededFromTemplate = false;
                scheduleSave(patch);
                if (lockEnabled) lock.touch();
                if (lockEnabled) markEditing();
              }}
              onMouseDown={() => {
                // Locked by someone else → pulse the take-over button so the
                // path forward is obvious (Walter 2026-07-04; mirrors ListPanel).
                if (!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe) flashTakeover();
              }}
              onFocus={async () => {
                setFormatTarget({
                  listId: list.id,
                  textareaRef,
                  commit: formatCommit,
                  isReminderList: isDesignatedReminderList(userPrefs, list?.id),
                  readOnly: effectiveReadOnly,
                  pushToast,
                  notifyContentChange: (prev, next) => { if (!effectiveReadOnly) reminderCapture.onContentChanged?.(prev, next); },
                });
                if (!lockEnabled) return;
                // Same rollback-on-takeover pattern as ListPanel.
                // acquire() → { acquired, contended }; only `contended`
                // (a fresh foreign lock seen by a completed server
                // transaction) is a real takeover. A false `contended`
                // is a transient failure — keep optimistic typing,
                // stay silent. See ListPanel for the longer comment.
                const snapshotAtFocus = list.content || '';
                const { acquired, contended } = await lock.acquire();
                if (acquired) return;
                if (!contended) return; // transient failure — keep optimistic typing
                setLocalContent(snapshotAtFocus);
                if (pendingPatch.current && pendingPatch.current.content !== undefined) {
                  pendingPatch.current = null;
                }
                if (saveTimer.current) {
                  clearTimeout(saveTimer.current);
                  saveTimer.current = null;
                }
                if (pushToast) {
                  pushToast('another device just took the lock — your last keystrokes weren\'t saved', null, { ttlMs: 4500 });
                }
              }}
              onKeyDown={(e) => {
                // Ctrl+H manual highlight (+ popup hotkeys). See ListPanel.
                if (highlight.onKeyDown(e)) return;
                // Walter 2026-05-18: Alt+Shift+↑/↓ moves the current
                // line (or selected block) up/down. See ListPanel for
                // rationale.
                if (e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                  if (effectiveReadOnly) return;
                  const ta = textareaRef.current;
                  if (!ta) return;
                  const fn = e.key === 'ArrowUp' ? moveLinesUp : moveLinesDown;
                  const r = fn(localContent, ta.selectionStart, ta.selectionEnd);
                  if (!r) { e.preventDefault(); return; }
                  e.preventDefault();
                  setLocalContent(r.nextValue);
                  scheduleSave({ content: r.nextValue });
                  requestAnimationFrame(() => {
                    const cur = textareaRef.current;
                    if (cur) cur.setSelectionRange(r.nextSelStart, r.nextSelEnd);
                  });
                  if (lockEnabled) lock.touch();
                  return;
                }
                reminderCapture.handleKeyDown(e);
              }}
              onBlur={(e) => {
                blurFormatTarget(list.id); // greys the global Format menu
                // Walter 2026-06-09: capture typed-but-not-Entered lines on
                // leave (see ListPanel). Skip when read-only.
                if (!effectiveReadOnly) {
                  reminderCapture.handleBlur(e);
                  // Don't strand a debounced reminder reconcile on the way out.
                  reminderCapture.flushReminderReconcile?.();
                }
                flushSave();
                // Release the lock on click-away — see ListPanel
                // for the full rationale (Walter 2026-05-04).
                if (lockEnabled && lock.heldByMe) lock.release();
              }}
              style={{
                flex: 1, position: 'relative', zIndex: 1,
                background: 'none', border: 'none', outline: 'none',
                // Walter 2026-05-26 demo-content treatment — see ListPanel
                // for rationale. Italic + dim while seededFromTemplate is
                // true; normalizes on first edit via the onChange's
                // flag-clear patch.
                color: list?.seededFromTemplate
                  ? 'var(--text-dim)'
                  : (isGuest && isDesignatedReminderList(userPrefs, list?.id))
                    ? 'var(--text-dim)'
                    : 'var(--text)',
                fontStyle: list?.seededFromTemplate ? 'italic' : 'normal',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(var(--note-font-size, 12px) + 1px)', lineHeight: 'var(--note-line-height, 1.5)',
                // Walter 2026-05-09 (round 2): the previous fix only
                // applied 14px padding to inline mode, but full-screen
                // tile mode (the X-tap path) was also clipping on iOS.
                // Now 14px everywhere on mobile-class widths so the
                // tile content gets max horizontal room. boxSizing
                // border-box + maxWidth 100% + minWidth 0 + overflow
                // wrapping safety nets together prevent any iOS Safari
                // textarea-intrinsic-width quirks from pushing content
                // past the parent.
                padding: (inline || fullScreen) ? '14px 14px' : '16px 24px',
                boxSizing: 'border-box',
                resize: 'none',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                userSelect: 'text',
                cursor: effectiveReadOnly ? 'not-allowed' : 'text',
                opacity: effectiveReadOnly ? 0.6 : 1,
                minHeight: 0,
              }}
              spellCheck={true}
            />
            {/* "Mark as done" veil — in-front backdrop instance (see ListPanel). */}
            <SyntaxBackdrop content={localContent} textareaRef={textareaRef} isReminderList={isDesignatedReminderList(userPrefs, list?.id)} variant="veil" />
            </>)}
            </div>
            {highlight.popup && (
              <HighlightPopup
                x={highlight.popup.x}
                y={highlight.popup.y}
                active={highlight.popup.active}
                fading={highlight.popup.fading}
              />
            )}
          </div>
        </div>

        {/* Floating outline FAB removed (round 5 cleanup): the
            OutlineBand below the header is the canonical affordance.
            If the header scrolls out of view on mobile, the user
            scrolls back up to reach it. */}
      </div>

      {/* Walter 2026-05-16: previous-versions side-by-side modal. */}
      <HistoryPanel
        open={showHistory}
        onClose={() => setShowHistory(false)}
        uid={list?._sharedFromOwnerUid || boardOwnerUid || currentUid}
        boardId={list?._sharedFromBoardId || currentBoardId}
        listId={list?.id}
        listTitle={list?.title}
        currentContent={localContent}
        currentTitle={list?.title}
        actorUid={currentUid}
        pushToast={pushToast}
        sourceList={list}
        onResolved={() => setDismissedConflictKey(flagKey(list?.conflictPendingReview))}
        onApplyContent={(newContent /* , newTitle */) => {
          // FocusedPanel only owns localContent (title lives in the
          // parent's list doc); apply just the content.
          if (typeof newContent === 'string') {
            setLocalContent(newContent);
            scheduleSave({ content: newContent });
          }
        }}
      />
    </>
  );
}

// ── Overflow-menu row ──────────────────────────────────────
// Single menu entry with hover highlight. `danger=true` uses the destructive
// accent color for delete actions.
function MenuRow({ children, onClick, danger, dim }) {
  const [hover, setHover] = useState(false);
  const base = danger
    ? 'var(--danger)'
    : (dim ? 'var(--text-dimmer)' : 'var(--text-dim)');
  const hi = danger
    ? 'var(--danger)'
    : (dim ? 'var(--text-dim)' : 'var(--text)');
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '10px 14px',
        cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: hover ? hi : base,
        background: hover ? 'var(--surface2)' : 'transparent',
        transition: 'background 0.1s, color 0.1s',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </div>
  );
}

// ── Rail row ───────────────────────────────────────────────
// Single row in the desktop FocusedPanel's left rail. Branches on
// entry.kind ('header' | 'date') so headers preserve the existing
// indent-by-level treatment and dates get a small `@` accent prefix
// with a formatted date+preview label.
function RailRow({ entry, onClick }) {
  const [hover, setHover] = useState(false);
  const isHeader = entry.kind === 'header';
  const baseColor = isHeader
    ? (entry.level === 1 ? 'var(--text)' : 'var(--text-dim)')
    : 'var(--text-dim)';
  const label = isHeader ? entry.text : formatDateAnchorLabel(entry);
  return (
    <div
      onClick={onClick}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '6px',
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingRight: '12px',
        paddingLeft: isHeader
          ? (12 + (entry.level - 1) * 14) + 'px'
          : '12px',
        fontFamily: 'var(--mono)',
        fontSize: isHeader && entry.level === 1
          ? 'calc(12px * var(--ui-scale, 1))'
          : 'calc(11px * var(--ui-scale, 1))',
        fontWeight: isHeader && entry.level === 1 ? 600 : 400,
        color: hover ? 'var(--accent)' : baseColor,
        background: hover ? 'var(--surface2)' : 'transparent',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'background 0.1s, color 0.1s',
      }}
    >
      {!isHeader && (
        <span style={{
          color: hover ? 'var(--accent)' : 'var(--accent)',
          opacity: hover ? 1 : 0.8,
          flexShrink: 0,
          fontWeight: 600,
        }}>@</span>
      )}
      <span style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    </div>
  );
}
